import { useQuery } from '@apollo/client';
import { AddressAutofill } from '@mapbox/search-js-react';
import {
  CheckboxInput,
  CountrySelect,
  FieldContainer,
  Input,
  RcSelect,
  RegionSelect,
} from '@retainerclub/common';
import { GET_ACCOUNT_ADDRESSES } from '@retainerclub/customer-api';
import { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import CheckoutFormSection from './CheckoutFormSection';

const NEW_ADDRESS_OPTION_ID = 'new-address';

function CheckoutFormAddress() {
  const { setValue, watch } = useFormContext();
  const { data, loading } = useQuery(GET_ACCOUNT_ADDRESSES);
  const shippingAddressId = watch('shippingAddress.id');
  const [useNewAddress, setUseNewAddress] = useState(false);

  const accountAddresses = data?.accountAddresses || [];
  const shippingAddresses = useMemo(() => {
    return accountAddresses
      .filter((address) => address.isShipping)
      .sort((a, b) => {
        // Sort by isDefault first, then by createdAt (latest first)
        if (a.isDefault && !b.isDefault) {
          return -1;
        }
        if (!a.isDefault && b.isDefault) {
          return 1;
        }
        return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
      });
  }, [accountAddresses]);

  const addressOptions = useMemo(() => {
    const options = shippingAddresses.map((address) => {
      const defaultLabel = address.isDefault ? ' (Default)' : '';
      const addressLine = address.address2
        ? `${address.address}, ${address.address2}`
        : address.address;
      return {
        id: address.id,
        name: `${addressLine}, ${address.city}, ${address.region} ${address.postalCode}${defaultLabel}`,
        address,
      };
    });

    options.push({
      id: NEW_ADDRESS_OPTION_ID,
      name: '+ Add New Address',
      address: null,
    });

    return options;
  }, [shippingAddresses]);

  const latestShippingAddress = useMemo(() => {
    return shippingAddresses[0];
  }, [shippingAddresses]);

  const hasShippingAddresses = shippingAddresses.length > 0;
  const showAddressForm =
    !hasShippingAddresses ||
    useNewAddress ||
    shippingAddressId === NEW_ADDRESS_OPTION_ID;

  useEffect(() => {
    if (
      hasShippingAddresses &&
      latestShippingAddress &&
      !shippingAddressId &&
      !useNewAddress
    ) {
      setValue('shippingAddress.id', latestShippingAddress.id);
      setUseNewAddress(false);
    }
  }, [
    hasShippingAddresses,
    latestShippingAddress,
    shippingAddressId,
    useNewAddress,
    setValue,
  ]);

  useEffect(() => {
    const currentId = shippingAddressId;
    if (currentId === NEW_ADDRESS_OPTION_ID) {
      setUseNewAddress(true);
      setValue('shippingAddress.address', '');
      setValue('shippingAddress.address2', '');
      setValue('shippingAddress.city', '');
      setValue('shippingAddress.region', '');
      setValue('shippingAddress.postalCode', '');
      setValue('shippingAddress.country', 'US');
      setValue('shippingAddress.isDefault', false);
    } else if (currentId && currentId !== NEW_ADDRESS_OPTION_ID) {
      setUseNewAddress(false);
      const selectedAddress = shippingAddresses.find(
        (address) => address.id === currentId,
      );
      if (selectedAddress) {
        setValue('shippingAddress.address', selectedAddress.address || '');
        setValue('shippingAddress.address2', selectedAddress.address2 || '');
        setValue('shippingAddress.city', selectedAddress.city || '');
        setValue('shippingAddress.region', selectedAddress.region || '');
        setValue(
          'shippingAddress.postalCode',
          selectedAddress.postalCode || '',
        );
        setValue('shippingAddress.country', selectedAddress.country || 'US');
      }
    }
  }, [shippingAddressId, setValue, shippingAddresses]);

  return (
    <CheckoutFormSection title="Shipping Information">
      {hasShippingAddresses && (
        <div className="sm:col-span-6">
          <FieldContainer
            name="shippingAddress.id"
            label="Shipping Address"
            required={!showAddressForm}
          >
            <RcSelect
              name="shippingAddress.id"
              data={addressOptions}
              disabled={loading}
              clearable={false}
            />
          </FieldContainer>
        </div>
      )}

      {showAddressForm && (
        <>
          <div className="sm:col-span-6">
            <AddressAutofill
              accessToken={import.meta.env.VITE_MAPBOX_ACCESS_TOKEN}
              confirmOnBrowserAutofill={{
                minimap: true,
                skipConfirmModal: (feature) =>
                  ['exact', 'high'].includes(
                    feature.properties.match_code.confidence,
                  ),
              }}
              onRetrieve={(address) => {
                if (address?.features?.length > 0) {
                  const {
                    country_code: countryCode,
                    region_code: regionCode,
                    matching_name: matchingName,
                  } = address.features[0].properties;

                  setValue(
                    'shippingAddress.country',
                    countryCode.toUpperCase(),
                  );

                  setValue('shippingAddress.region', regionCode.toUpperCase());

                  setValue('shippingAddress.address', matchingName);
                }
              }}
              onChange={(event) => {
                setValue('shippingAddress.address', event);
              }}
              options={{
                country: 'US',
                language: 'en',
                proximity: 'ip',
              }}
            >
              <Input
                name="shippingAddress.address"
                label="Address"
                required
                autoComplete=""
              />
            </AddressAutofill>
          </div>

          <div className="sm:col-span-3">
            <Input
              name="shippingAddress.address2"
              label="Address Line 2"
              autoComplete="address-line2"
            />
          </div>
          <div className="sm:col-span-3">
            <Input
              name="shippingAddress.city"
              label="City"
              required
              autoComplete="address-level2"
            />
          </div>
          <div className="sm:col-span-2">
            <CountrySelect
              name="shippingAddress.country"
              required
              autoComplete=""
              usOnly
            />
          </div>
          <div className="sm:col-span-2">
            <RegionSelect
              name="shippingAddress.region"
              countryFieldName="shippingAddress.country"
              autoComplete=""
            />
          </div>
          <div className="sm:col-span-2">
            <Input
              name="shippingAddress.postalCode"
              label="Postal Code"
              required
              autoComplete="postal-code"
            />
          </div>
          <div className="sm:col-span-6">
            <div className="mt-4 sm:col-span-full">
              <CheckboxInput
                name="shippingAddress.isDefault"
                label="Default?"
                description="Make this the default shipping address?"
              />
            </div>
          </div>
        </>
      )}
    </CheckoutFormSection>
  );
}

export default CheckoutFormAddress;
